<template>
  <div class="line-chart chart-container" ref="container">
    <h3 v-if="title">{{ title }}</h3>

    <SimpleDialog
      v-if="description"
      :open="showPopup"
      title="Events AI Emotions"
      :dark="darkDialog"
    >
      <p v-html="description"></p>
    </SimpleDialog>

    <v-skeleton-loader
      v-if="isLoading"
      type="image"
      alt="Getting stats..."
    ></v-skeleton-loader>
    <div
      v-if="chartOptions && chartOptions.xAxis && !isLoading && !isEmpty"
      class="btn-ctn-ctn"
    >
      <div>
        <v-icon
          v-if="description"
          small
          color="blue"
          v-on:click.stop.prevent="showPopup = !showPopup"
          title="Read description"
          class="description-icon"
          >fas fa-info-circle</v-icon
        >
        <v-btn
          v-if="clickedDate"
          class="today-button"
          x-small
          outlined
          @click="resetSelectedDate"
          aria-label="Reset the page to today"
          >Today</v-btn
        >
      </div>

      <v-chart
        ref="chartInstance"
        @finished="chartFinished"
        :option="chartOptions"
        :style="sizeCSS()"
      />
    </div>
    <p v-if="isEmpty" class="message">There are no data to show.</p>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from "echarts/components";

import { LineChart } from "echarts/charts";
import { CHART_TOOLTIP } from "@/common/constants/charts";
use([
  CanvasRenderer,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  LineChart,
  DataZoomComponent,
]);

const EMITTED_ACTIONS = {
  FINISHED: "finished",
  CLICKED_DATE: "clicked-date",
  RESET_DATE: "reset-date",
};

export default {
  name: "LineGraph",
  components: {
    SimpleDialog: () => import("@/components/common/dialogs/SimpleDialog"),
  },
  props: {
    title: {
      type: String,
      required: false,
    },
    symbol: {
      type: String,
      default: "none",
    },
    height: {
      type: Number,
      default: 0,
    },
    width: {
      type: String,
      default: "100%",
    },
    chartData: {
      type: Object,
      required: true,
    },
    legend: {
      type: Array,
    },
    preselectedLegend: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    filterSelection: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Specify a tooltip formatter to use for this chart. The default charting library tooltip will be
     * used if this prop is not passed in.
     */
    tooltipFormatterName: {
      type: String,
      validator: (value) => {
        //The value must be a function on this component
        return ["tooltipFormatterPercentages"].includes(value);
      },
    },
    enableClickDate: {
      type: Boolean,
      default: false,
    },
    preclickedDate: {
      type: [String, Object],
      default: null,
      validator: (d) => {
        return d === null || d.match(/^[a-zA-Z]{3} \d{1,2}$/)?.length;
      },
    },
    refresh: {
      type: String,
      default: null,
    },
    ignoreAppHeight: {
      type: Boolean,
      default: true,
    },
    description: {
      type: String,
      required: false,
      default: "",
    },
    darkDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      clickedDate: null,
      maxValue: 0,
      gridHeight: 170,
      lastSize: "",
      showPopup: false,
    };
  },
  computed: {
    chartOptions() {
      return {
        series: this.getSeries(),
        xAxis: {
          type: "category",
          data: this.xAxis,
          boundaryGap: false,
          axisLabel: {
            color: "white",
            formatter: (v) =>
              this.formatDate(v, {
                timeBinSize: this.chartData?.metadata?.timeBinSize,
              }),
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            color: "white",
          },
          splitLine: {
            show: true,
            lineStyle: { color: "rgba(255,255,255,0.2)" },
          },
          minInterval: 1,
          min: 0,
        },
        dataZoom: {
          id: "dataZoomX",
          type: "slider",
          start: 0,
          end: 100,
          height: 20,
          filterMode: "filter",
          bottom: 10,
          fillerColor: "rgba(255,255,255,0.2)",
          borderColor: "rgba(255,255,255,0.2)",
          moveHandleSize: 0,
        },
        tooltip: Object.assign(CHART_TOOLTIP, {
          trigger: "axis",
          formatter: this.tooltipFormatter,
          axisPointer: {
            type: "shadow",
            axis: "x",
          },
        }),
        grid: {
          top:
            this.chartData?.seriesData?.length >= 9
              ? "100"
              : this.chartData?.seriesData?.length > 6
              ? "75"
              : "60",
          left: this.gridLeft,
          right: 25,
          bottom: 75,
          height: this.gridHeight,
        },
        legend: {
          data: this.legend || this.getLegend(this.chartData?.seriesData),
          icon: "roundRect",
          itemHeight: 10,
          itemWidth: 18,
          left: 50,
          width: "70%",
          orient: "horizontal",
          textStyle: {
            color: "white",
          },
          selected: this.filterSelection
            ? this.getPreselectedLegend(
                this.chartData?.seriesData,
                this.filterSelection
              )
            : this.preselectedLegend,
        },
      };
    },
    xAxis() {
      return this.chartData?.xAxis?.map((x) => x);
    },
    isLoading() {
      return !Array.isArray(this.chartData?.seriesData);
    },
    isEmpty() {
      return this.chartData?.seriesData?.length === 0;
    },
    tooltipFormatter() {
      //Return a reference to the tooltip formatter function to use.
      //The name of the formatter can be passed in as a prop
      return this[this.tooltipFormatterName] || null;
    },
    gridLeft() {
      let left = this.maxValue.toString().length * 10 + 10;
      let min = 35;
      return left > min ? left : min;
    },
  },
  mounted() {
    this.usePreclickedDate();
    this.setGridHeight();
    window.addEventListener("resize", this.handleResize);
  },
  methods: {
    sizeCSS() {
      let height = this.height;
      if (!height) {
        height =
          this.chartData?.seriesData?.length >= 10
            ? this.gridHeight + 130
            : this.gridHeight + 120;
      }
      return `height: ${height}px; width:${this.width}`;
    },
    chartFinished() {
      this.$emit(EMITTED_ACTIONS.FINISHED, true);

      if (this.enableClickDate && this.$refs?.chartInstance?.chart) {
        const zr = this.$refs.chartInstance.chart.getZr();
        zr.on("click", this.handleChartClick);
      }

      this.reloadChart();
    },
    reloadChart() {
      if (this.$refs.chartInstance && this.lastSize !== this.sizeCSS()) {
        this.lastSize = this.sizeCSS();
        this.$refs.chartInstance.resize();
      }
    },
    getLegend(data) {
      if (!data) {
        return [];
      }
      let ret = [];
      data.forEach((x) => {
        ret.push(x.name);
      });
      return ret;
    },
    getSeries() {
      let max = 0;
      let series = this.chartData?.seriesData;
      if (!series) {
        return;
      }

      //If there is only one day in the time series, make sure we are using a visual symbol.
      const symbol =
        this.xAxis?.length === 1 && this.symbol === "none"
          ? "circle"
          : this.symbol;

      series.forEach((s) => {
        s.symbol = symbol;
        s.markLine = this.getMarkLine();

        let seriesMax = Math.max(...s.data);
        max = seriesMax > max ? seriesMax : max;
      }, this);

      this.maxValue = max;
      return series;
    },
    setGridHeight() {
      if (!this.ignoreAppHeight && window.innerHeight < 950) {
        this.gridHeight = 100;
      } else {
        this.gridHeight = 140;
      }
    },
    /**
     * Generates HTML for the tooltip that shows each series data value as a percentage.
     * @param allSeries - See eCharts docs (https://echarts.apache.org/en/option.html#tooltip.formatter)
     * @return {string}
     */
    tooltipFormatterPercentages(allSeries) {
      let tooltipHTML = "";

      //Show the data (or x axis) at the top
      let title = "";
      try {
        if (
          this.chartData?.metadata?.timeBinSize === "1M" ||
          this.chartData?.metadata?.timeBinSize === "month"
        ) {
          title = dayjs(allSeries[0].axisValue).format("MMMM YYYY");
        } else {
          title = dayjs(allSeries[0].axisValue).format("MMMM D, YYYY");
        }
      } catch (e) {
        console.error(
          "Could not format date for chart tooltip. The xAxis is likely not a date value."
        );
      }
      tooltipHTML += `${title}`;

      let totalSeries = allSeries.find((s) => s.seriesName === "Total");
      let total;

      //Show the total first
      if (totalSeries) {
        total = totalSeries.data;
        tooltipHTML += `<div style="width:100%">${
          totalSeries.marker
        } Total <strong style="float:right; margin-left:5px;">${total.toLocaleString()}</strong> </div>`;
        allSeries.splice(allSeries.indexOf(totalSeries), 1);
      }

      //Show the data value and percentage of each active series
      allSeries.forEach((series) => {
        tooltipHTML += `<div style="width:100%">${series.marker} ${
          series.seriesName
        } <strong style="float:right; margin-left:5px;">${series.data.toLocaleString()}`;

        if (total) {
          tooltipHTML += ` (${Math.round((series.data / total) * 100)}%) <br/>`;
        }

        tooltipHTML += `</strong></div>`;
      });

      return tooltipHTML;
    },
    handleChartClick(params) {
      //Get the x,y position where the click happened
      const pointInPixel = [params.offsetX, params.offsetY];
      //Get the x axis value at this point
      const pointInGrid = this.$refs.chartInstance.chart.convertFromPixel(
        "grid",
        pointInPixel
      );
      if (
        this.$refs.chartInstance.chart.containPixel("grid", pointInPixel) &&
        this.xAxis.length >= pointInGrid[0]
      ) {
        this.clickedDate = this.xAxis[pointInGrid[0]];

        //Add the year to the date when emitting
        let date = this.xAxis[pointInGrid[0]];
        if (date.match(/^[a-zA-Z]{3} \d{1,2}$/)?.length) {
          date += " " + new Date().getUTCFullYear();
        }
        this.$emit(EMITTED_ACTIONS.CLICKED_DATE, date);
      }
    },
    resetSelectedDate() {
      this.clickedDate = null;
      this.$emit(EMITTED_ACTIONS.RESET_DATE);
    },
    formatDate(date, options = {}) {
      if (options?.timeBinSize === "1M" || options?.timeBinSize === "month") {
        return dayjs(date).format("MMM YYYY");
      } else {
        return dayjs(date).format("MMM D");
      }
    },
    unformatDate(date) {
      //Revert the date string to the format used in the xAxis data
      if (
        this.chartData?.metadata?.timeBinSize === "1M" ||
        this.chartData?.metadata?.timeBinSize === "month"
      ) {
        return dayjs(date).format("YYYY-MM-DD");
      } else {
        //Get the year from the xAxis
        const year = this.xAxis?.length
          ? dayjs(this.xAxis[0]).get("year")
          : null;
        if (year) {
          return dayjs(date).set("year", year).format("YYYY-MM-DD");
        }
      }
    },
    usePreclickedDate() {
      if (this.preclickedDate) {
        if (this.clickedDate !== this.unformatDate(this.preclickedDate)) {
          this.clickedDate = this.unformatDate(this.preclickedDate);
        }
      } else {
        this.clickedDate = null;
      }
    },
    getMarkLine() {
      if (this.enableClickDate) {
        return {
          symbol: "none",
          data: [
            {
              //If there is no clicked date, move the markline to -1 so it's invisible
              //I was having a terrible time getting echarts to remove the markline on redraw, so this works
              xAxis: this.clickedDate || -1,
              clickable: false,
              zlevel: 101,
              silent: true,
              animation: false,
              symbol: "none",
              label: {
                show: true,
                position: "middle",
                borderWidth: 0,
                color: "white",
                textBorderType: "solid",
                textBorderWidth: 5,
                textBorderColor: "#191a1a",
                formatter: (v) => {
                  return this.formatDate(v.data.value, {
                    timeBinSize: this.chartData?.metadata?.timeBinSize,
                  });
                },
              },
              lineStyle: {
                type: "solid",
                color: "grey",
                width: 2,
              },
            },
          ],
        };
      } else {
        return {};
      }
    },
    handleResize() {
      this.setGridHeight();
      this.reloadChart();
    },
    getPreselectedLegend: (seriesData, filterSelection) => {
      if (!seriesData) {
        return {};
      }
      let selected = {};
      seriesData?.forEach((series) => {
        selected[series.name] =
          filterSelection.length === 0 ||
          filterSelection.includes(series.id) ||
          series.id === "total";
      });
      return selected;
    },
  },
  watch: {
    refresh: {
      handler() {
        this.reloadChart();
      },
    },
    preclickedDate: {
      handler() {
        this.usePreclickedDate();
        this.reloadChart();
      },
    },
  },
};
</script>

<style scoped>
.line-chart {
  color: #fff;
  overflow: hidden;
}
h3 {
  margin-bottom: 15px;
  display: inline-block;
}
.btn-ctn-ctn {
  position: relative;
}
.button-container {
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  position: absolute;
  right: 10px;
}
.today-button {
  margin-left: 10px;
}
.message {
  margin-top: 20px;
  margin-bottom: 40px;
  color: #ccc;
  font-size: 0.9em;
}
.description-icon {
  margin-left: 10px;
  position: absolute;
  z-index: 10;
}
</style>
